import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useData } from '../../contexts/DataContext';
import MobileLayout from '../layout/MobileLayout';
import MobileBottomNav from '../layout/MobileBottomNav';
import { LoadingSpinner } from '../common/LoadingSpinner';
import Button from '../common/Button';
import ShareSheet from '../common/ShareSheet';
import TaskCompletionModal from './TaskCompletionModal';
import AddTaskModal from './AddTaskModal';
import EditTaskModal from './EditTaskModal';
import FileViewerModal from '../files/FileViewerModal';
import FollowUpTasksModal from './FollowUpTasksModal';
import { DetailPaneShell } from '../common/DetailPaneShell';
import { toast } from 'sonner';
import { confirmAction } from '../../utils/confirmDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { isTaskOverdue } from '../../utils/dateUtils';
import { PaperPlaneTilt } from '@phosphor-icons/react';
import { MessageContactModal } from './MessageContactModal';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { TaskMessageHistory } from './TaskMessageHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_CARD = 'bg-white rounded-xl border border-blue-200/80 p-5 mb-4 shadow-sm space-y-3';
const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wider text-slate-400';
const FOOTER_BTN = 'h-8 px-3 text-xs font-medium inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 transition-colors active:scale-95';
const FOOTER_BTN_COMPLETE = 'h-8 px-3 text-xs font-medium inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-colors active:scale-95';
const BADGE = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border';
const PRIORITY_BADGE = {
  High: `${BADGE} bg-rose-50 text-rose-700 border-rose-200`,
  Medium: `${BADGE} bg-amber-50 text-amber-700 border-amber-200`,
  Low: `${BADGE} bg-emerald-50 text-emerald-700 border-emerald-200`,
};
const CHIP = 'inline-flex flex-shrink-0 items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border';
const STATUS_CHIP = {
  completed: `${CHIP} bg-emerald-50 text-emerald-700 border-emerald-200`,
  overdue: `${CHIP} bg-rose-50 text-rose-700 border-rose-200`,
  pending: `${CHIP} bg-indigo-50 text-indigo-700 border-indigo-200`,
};
const STATUS_DOT = { completed: 'bg-emerald-500', overdue: 'bg-rose-500', pending: 'bg-indigo-500' };

const CollapsibleSection = ({ id, label, open, onToggle, testId, children }) => (
  <div id={id} className="bg-white rounded-xl border border-blue-200/80 mb-4 shadow-sm" data-testid={testId}>
    <button
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50/70 rounded-xl transition-colors"
      data-testid={`${testId}-toggle`}
    >
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <svg className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {open && <div className="px-5 pb-5 space-y-3" data-testid={`${testId}-list`}>{children}</div>}
  </div>
);

const DetailItem = ({ label, children, wide = false, valueClass = 'text-slate-900', testId }) => (
  <div className={`p-3 rounded-lg bg-slate-50/80 border border-slate-100 ${wide ? 'sm:col-span-2' : ''}`} data-testid={testId}>
    <dt className="text-xs font-medium text-slate-500">{label}</dt>
    <dd className={`mt-0.5 text-sm font-medium break-words ${valueClass}`}>{children}</dd>
  </div>
);

const TaskDetailsPage = ({ taskId: paneTaskId, embedded = false, onClose, onBack, backLabel, onSelectTask, onOpenContact, onChanged }) => {
  const params = useParams();
  const taskId = paneTaskId || params.taskId;
  const navigate = useNavigate();
  const { t: translate } = useLanguage();
  const tr = (key, fallback) => (translate(key) === key ? fallback : translate(key));
  const goToTask = (id) => (embedded ? onSelectTask(id) : navigate(`/tasks/${id}`));
  const finishAndLeave = (path) => {
    if (embedded) {
      onChanged?.();
      onClose?.();
    } else {
      navigate(path);
    }
  };
  const { t } = useLanguage();
  const { archiveTask, restoreTask, deleteTask } = useData();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [contact, setContact] = useState(null);
  const [showMessageContact, setShowMessageContact] = useState(false);
  const isDesktop = useIsDesktop();
  const { activeWorkspace } = useWorkspace() || {};
  const canMessageContact = isDesktop && activeWorkspace?.kind === 'org';
  const [messageHistoryKey, setMessageHistoryKey] = useState(0);
  const [followUpTasks, setFollowUpTasks] = useState([]);
  const [recurringTasks, setRecurringTasks] = useState([]);
  // Share sheet (popover on desktop, bottom sheet on mobile).
  const [shareOpen, setShareOpen] = useState(false);
  const shareBtnRef = useRef(null);
  const [showFollowUpTasksModal, setShowFollowUpTasksModal] = useState(false);
  const [followUpsOpen, setFollowUpsOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const fetchTask = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/tasks/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setTask(response.data);
      
      // Fetch contact details if task has a contact (non-blocking)
      if (response.data.contact_id) {
        fetchContact(response.data.contact_id);
      }
      
      // Fetch follow-up tasks (non-blocking)
      fetchFollowUpTasks(response.data.id);
      
      // Automatically fetch recurring tasks if this is part of a recurring group
      if (response.data.recurring || response.data.parent_recurring_task_id) {
        const recurringGroupId = response.data.parent_recurring_task_id || response.data.id;
        console.log('🔄 Task is part of recurring group:', {
          taskId: response.data.id,
          taskTitle: response.data.title,
          isRecurring: response.data.recurring,
          parentRecurringTaskId: response.data.parent_recurring_task_id,
          recurringGroupId
        });
        fetchRecurringTasks(recurringGroupId);
      } else {
        console.log('❌ Task is not part of a recurring group:', {
          taskId: response.data.id,
          taskTitle: response.data.title,
          isRecurring: response.data.recurring,
          parentRecurringTaskId: response.data.parent_recurring_task_id
        });
      }
      
    } catch (error) {
      console.error('Error fetching task:', error);
      if (error.response?.status === 404) {
        setError('Task not found');
      } else if (error.response?.status === 403) {
        setError('Access denied');
      } else {
        setError('Failed to load task details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContact = async (contactId) => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setContact(response.data);
    } catch (error) {
      console.error('Error fetching contact:', error);
      // Don't set error for contact fetch failure, just leave contact as null
    }
  };

  const fetchFollowUpTasks = async (parentTaskId) => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Filter tasks that have this task as parent
      const followUps = response.data.filter(task => task.parent_task_id === parentTaskId);
      setFollowUpTasks(followUps);
    } catch (error) {
      console.error('Error fetching follow-up tasks:', error);
      // Don't set error, just leave followUpTasks as empty array
    }
  };

  const fetchRecurringTasks = async (recurringGroupId) => {
    try {
      console.log('🔄 Fetching recurring tasks for group ID:', recurringGroupId);
      
      const response = await axios.get(`${API}/tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          all: true // Get all tasks to find the recurring group
        }
      });
      
      console.log('📋 All tasks received:', response.data.length);
      console.log('🔍 Looking for tasks with parent_recurring_task_id or id matching:', recurringGroupId);
      
      // Filter tasks that are part of the same recurring group
      const recurringGroupTasks = response.data.filter(task => {
        const isInGroup = task.parent_recurring_task_id === recurringGroupId || 
                         (task.id === recurringGroupId && task.recurring);
        
        if (isInGroup) {
          console.log('✅ Found recurring task:', {
            id: task.id,
            title: task.title,
            recurring: task.recurring,
            parent_recurring_task_id: task.parent_recurring_task_id
          });
        }
        
        return isInGroup;
      });
      
      console.log(`🔄 Found ${recurringGroupTasks.length} tasks in recurring group`);
      
      // Sort with the original recurring task (the one with task.recurring = true) first
      recurringGroupTasks.sort((a, b) => {
        // Original recurring task first
        if (a.recurring && !b.recurring) return -1;
        if (!a.recurring && b.recurring) return 1;
        
        // Then sort by creation date (earliest first)
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      });
      
      console.log('🎯 Final recurring tasks list:', recurringGroupTasks.map(t => ({
        id: t.id,
        title: t.title,
        recurring: t.recurring
      })));
      
      setRecurringTasks(recurringGroupTasks);
    } catch (error) {
      console.error('❌ Error fetching recurring tasks:', error);
      // Don't set error, just leave recurringTasks as empty array
    }
  };

  const handleBack = () => {
    // Smart navigation: always go back to tasks list (parent page)
    // Use replace to avoid adding to history stack
    if (embedded) onClose?.();
    else navigate('/', { replace: true });
  };

  const renderLayout = (children, rightAction) => (embedded ? (
    <DetailPaneShell
      title={t('taskDetails')}
      onClose={onClose}
      onBack={onBack}
      backLabel={backLabel || tr('backToContact', 'Back to contact')}
      rightAction={rightAction}
    >
      {children}
    </DetailPaneShell>
  ) : (
    <MobileLayout title={t('taskDetails')} showBack onBack={handleBack} rightAction={rightAction} onRefresh={() => fetchTask(true)}>{children}</MobileLayout>
  ));

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    fetchTask(); // Refresh task data after edit
    onChanged?.();
  };

  // -----------------------------------------------------------------
  // Sharing — fetch the file from the API with auth, then trigger a
  // browser download / copy-to-clipboard. Backend endpoints are defined
  // in /app/backend/sharing.py.
  // -----------------------------------------------------------------
  const downloadFromApi = async (path, fallbackName) => {
    const resp = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!resp.ok) throw new Error(`Server responded ${resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleShareIcs = async () => {
    try {
      const safe = (task?.title || 'task').replace(/[^\w-]+/g, '_');
      await downloadFromApi(`/tasks/${taskId}/share/ics`, `${safe}.ics`);
    } catch (err) {
      console.error('ICS share failed:', err);
      alert('Could not generate calendar invite.');
    }
  };

  const handleCopyShareLink = async () => {
    try {
      const resp = await fetch(`${API}/tasks/${taskId}/share-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await resp.json();
      if (!data?.url) throw new Error('No url returned');
      try {
        await navigator.clipboard.writeText(data.url);
        alert(t('linkCopied') || 'Link copied to clipboard');
      } catch (_e) {
        // clipboard write blocked → fall back to prompt so the user can copy manually
        window.prompt(t('copyShareLink') || 'Copy share link', data.url);
      }
    } catch (err) {
      console.error('Share link failed:', err);
      alert(t('linkCopyFailed') || 'Could not copy link');
    }
  };

  const handleArchive = async () => {
    if (!(await confirmAction({
      title: tr('archiveThisTaskQ', 'Archive this task?'),
      message: tr('archiveTaskHint', 'You can restore it any time from Archives.'),
      confirmLabel: tr('archive', 'Archive'),
    }))) {
      return;
    }

    try {
      await archiveTask(taskId);
      toast.success(t('taskArchivedSuccessfully') || 'Task archived successfully');
      finishAndLeave('/');
    } catch (error) {
      console.error('Error archiving task:', error);
      toast.error('Failed to archive task');
    }
  };

  const handleRestore = async () => {
    if (!(await confirmAction({
      title: tr('restoreThisTaskQ', 'Restore this task?'),
      message: tr('restoreTaskHint', 'It will move back to your active tasks.'),
      confirmLabel: tr('restore', 'Restore'),
    }))) {
      return;
    }

    try {
      await restoreTask([taskId]);
      toast.success(t('taskRestoredSuccessfully') || 'Task restored successfully');
      finishAndLeave('/archives');
    } catch (error) {
      console.error('Error restoring task:', error);
      toast.error('Failed to restore task');
    }
  };

  const handleDelete = async () => {
    if (!(await confirmAction({
      title: tr('deleteThisTaskQ', 'Delete this task?'),
      message: tr('deleteTaskHint', 'This cannot be undone.'),
      confirmLabel: tr('delete', 'Delete'),
      destructive: true,
    }))) {
      return;
    }

    try {
      await deleteTask(taskId);
      toast.success(tr('taskDeletedShort', 'Task deleted'));
      finishAndLeave('/');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleCompleteTask = () => {
    setShowCompletionModal(true);
  };

  const handleCompletionSuccess = () => {
    setShowCompletionModal(false);
    fetchTask(); // Refresh task data to show completion details
    onChanged?.();
  };

  const handleCreateFollowUp = () => {
    setShowFollowUpModal(true);
  };

  const handleFollowUpSuccess = () => {
    setShowFollowUpModal(false);
    fetchFollowUpTasks(taskId);
    onChanged?.();
  };

  const handleRecurringClick = () => {
    // Scroll to recurring tasks section instead of navigating away
    setRecurringOpen(true);
    setTimeout(() => document.getElementById('recurring-tasks-section')?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const handleFileClick = (file) => {
    console.log('🔍 File clicked - Full object:', file);
    console.log('🔍 File properties:', {
      filename: file.filename,
      id: file.id,
      content_type: file.content_type,
      size_mb: file.size_mb,
      has_content: !!file.content
    });
    
    // Add task_id to the file object for the FileViewerModal
    const fileWithTaskId = {
      ...file,
      task_id: taskId // Add the current task ID
    };
    
    setCurrentFile(fileWithTaskId);
    setShowFileViewer(true);
  };

  if (loading) {
    return renderLayout(
      <>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
        {!embedded && <MobileBottomNav />}
      </>
    );
  }

  if (error || !task) {
    return renderLayout(
      <>
        <div className="mobile-card p-8 text-center">
          <div className="text-6xl mb-4">😞</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('taskNotFound')}</h3>
          <p className="text-gray-600 mb-4">{error || t('taskNotFoundDesc')}</p>
          <Button onClick={handleBack}>
            Go Back
          </Button>
        </div>
        {!embedded && <MobileBottomNav />}
      </>
    );
  }

  const status = task.completed ? 'completed' : isTaskOverdue(task) ? 'overdue' : 'pending';

  const headerActions = (
        <div className="flex items-center space-x-2 relative">
          {/* Share button — opens the ShareSheet (popover on desktop, sheet on mobile) */}
          <button
            ref={shareBtnRef}
            onClick={() => setShareOpen((v) => !v)}
            className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
            title={t('share') || 'Share'}
            aria-label={t('share') || 'Share'}
            data-testid="task-share-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>
          <ShareSheet
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            anchorRef={shareBtnRef}
            title={t('shareThisTask') || 'Share this task'}
            options={[
              {
                id: 'ics',
                icon: '📅',
                label: t('sendAsCalendarInvite') || 'Send as calendar invite',
                description: t('sendAsCalendarInviteDesc') || 'Opens in Google, Outlook or Apple Calendar',
                onClick: handleShareIcs,
              },
              {
                id: 'link',
                icon: '🔗',
                label: t('copyShareLink') || 'Copy share link',
                description: t('copyShareLinkDesc') || 'Anyone with the link can view, read-only',
                onClick: handleCopyShareLink,
              },
            ]}
          />
          {task.archived ? (
            // Show Restore button for archived tasks
            <button
              onClick={handleRestore}
              className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
              title={t('restoreTask') || 'Restore'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          ) : task.completed ? (
            // Show Archive button for completed (non-archived) tasks
            <button
              onClick={handleArchive}
              className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
              title={t('archiveTask')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </button>
          ) : (
            // Show Edit button for active tasks
            <button
              onClick={handleEdit}
              className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
              title={t('edit')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          <button
            onClick={handleDelete}
            className="text-white hover:text-red-200 lg:text-gray-600 lg:hover:text-red-600 transition-colors p-1"
            title={t('delete')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
  );

  return renderLayout(
    <>
      <div className="lg:max-w-5xl lg:mx-auto task-details-scale">
      {/* Task Header */}
      <div className="task-details-noscale bg-white rounded-xl border border-blue-200/80 p-5 lg:p-6 mb-4 shadow-sm" data-testid="task-details-header">
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight break-words min-w-0">
            {task.title}
          </h1>
          <span className={STATUS_CHIP[status]} data-testid="task-details-status-chip">
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
            {status === 'completed' ? t('completedLabel') : status === 'overdue' ? 'Overdue' : 'Pending'}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className={PRIORITY_BADGE[task.priority] || PRIORITY_BADGE.Medium}>
            {task.priority} {t('priorityLabel')}
          </span>
          {task.category && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {task.category}
            </span>
          )}
          {(task.recurring || task.parent_recurring_task_id) && (
            <button
              onClick={handleRecurringClick}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
              title={t('jumpToRecurringSection')}
            >
              🔄 {t('recurring')}
            </button>
          )}
          {followUpTasks.length > 0 && (
            <button
              onClick={() => setShowFollowUpTasksModal(true)}
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors"
              title={`${t('thisTaskHasFollowUps')} ${followUpTasks.length} follow-up task${followUpTasks.length !== 1 ? 's' : ''}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span>{followUpTasks.length} {followUpTasks.length === 1 ? (t('followUp') === 'followUp' ? 'follow-up' : t('followUp')) : t('followUps')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Task Details */}
      <div className={SECTION_CARD}>
        <h2 className={SECTION_LABEL}>Details</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {task.due_date && (
            <DetailItem label="Due" testId="task-details-due">
              {new Date(task.due_date).toLocaleDateString()}
              {task.time && ` · ${task.time}`}
            </DetailItem>
          )}
          {task.contact_id && (
            <DetailItem label="Contact">
              <button
                onClick={() => (embedded ? onOpenContact(task.contact_id) : navigate(`/contacts/${task.contact_id}`))}
                data-testid="task-details-contact-link"
                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-left"
              >
                {contact ? contact.name : `Contact #${task.contact_id}`}
              </button>
            </DetailItem>
          )}
          {task.location && <DetailItem label="Location" wide>{task.location}</DetailItem>}
          <DetailItem label="Created">{new Date(task.created_at).toLocaleDateString()}</DetailItem>
          {task.completed_at && (
            <DetailItem label="Completed" valueClass="text-emerald-700">
              {new Date(task.completed_at).toLocaleDateString()}
            </DetailItem>
          )}
        </dl>

        <div className="space-y-3">
          {/* Message contact — shown at the bottom of the details list so it
              doesn't crowd the contact name row on narrow mobile screens. */}
          {canMessageContact && task.contact_id && contact && (
            <div className="pt-1">
              <button
                onClick={() => setShowMessageContact(true)}
                data-testid="task-details-message-contact-btn"
                className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-white text-xs font-medium text-indigo-700 hover:bg-indigo-50 transition-colors"
              >
                <PaperPlaneTilt size={14} weight="bold" />Message contact
              </button>
            </div>
          )}
          
          {/* Reminder Status — mobile only */}
          {!isDesktop && (
          <div className="flex items-center pt-3 border-t border-slate-100" data-testid="task-details-reminder-status">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="reminder-status"
                checked={task.reminder_enabled || false}
                disabled
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-not-allowed opacity-60"
              />
              <label htmlFor="reminder-status" className="ml-3">
                <span className="font-medium text-gray-900">⏰ {t('enableReminder') || 'Reminder'}</span>
                {task.reminder_enabled && (
                  <p className="text-sm text-gray-600 mt-1">
                    {task.reminder_sent 
                      ? '✅ Reminder sent' 
                      : '⏳ Reminder will be sent 1 hour before due time'}
                  </p>
                )}
                {!task.reminder_enabled && (
                  <p className="text-sm text-gray-600 mt-1">
                    No reminder set for this task
                  </p>
                )}
              </label>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {task.notes && (
        <div className={SECTION_CARD}>
          <h2 className={SECTION_LABEL}>Notes</h2>
          <p className="text-gray-700 leading-relaxed">{task.notes}</p>
        </div>
      )}

      <TaskMessageHistory taskId={task.id} refreshKey={messageHistoryKey} />

      {/* Task Attachments (excluding completion attachments) */}
      {task.attachments && task.attachments.filter(att => att.file_type !== 'completion').length > 0 && (
        <div className={SECTION_CARD}>
          <h2 className={SECTION_LABEL}>
            Task Attachments ({task.attachments.filter(att => att.file_type !== 'completion').length})
          </h2>
          <div className="space-y-3">
            {task.attachments.filter(att => att.file_type !== 'completion').map((attachment, index) => (
              <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{attachment.filename}</h3>
                  <p className="text-sm text-gray-500">
                    {attachment.content_type} • {attachment.size_mb} MB
                  </p>
                </div>
                <button 
                  className="ml-3 text-blue-600 hover:text-blue-800"
                  onClick={() => handleFileClick(attachment)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completion Details - MOVED TO BOTTOM */}
      {task.completed && (task.completion_notes || task.completed_at || (task.attachments && task.attachments.some(att => att.file_type === 'completion'))) && (
        <div className={SECTION_CARD}>
          <h2 className={SECTION_LABEL}>Completion Details</h2>
          
          <div className="space-y-3">
            {task.completed_at && (
              <dl>
                <DetailItem label="Completed on" valueClass="text-emerald-700">
                  {new Date(task.completed_at).toLocaleDateString()} · {new Date(task.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </DetailItem>
              </dl>
            )}
            
            {task.completion_notes && (
              <div>
                <p className="text-xs font-medium text-slate-500">Notes</p>
                <p className="text-sm text-slate-800 mt-1 leading-relaxed">
                  {task.completion_notes}
                </p>
              </div>
            )}

            {/* Completion Attachments */}
            {task.attachments && task.attachments.filter(att => att.file_type === 'completion').length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500">Attachments</p>
                <div className="mt-2 space-y-2">
                  {task.attachments.filter(att => att.file_type === 'completion').map((attachment, index) => (
                    <div key={index} className="flex items-center p-3 bg-slate-50/80 rounded-lg border border-slate-100">
                      <div className="flex-shrink-0 mr-3">
                        {attachment.content_type?.startsWith('image/') ? '🖼️' :
                         attachment.content_type?.startsWith('audio/') ? '🎵' :
                         attachment.content_type?.includes('pdf') ? '📄' :
                         attachment.content_type?.includes('document') || attachment.content_type?.includes('word') ? '📝' : '📎'}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{attachment.filename}</h4>
                        <p className="text-sm text-gray-600">
                          {attachment.content_type} • {attachment.size_mb ? `${attachment.size_mb} MB` : 'Unknown size'}
                        </p>
                      </div>
                      <button 
                        className="ml-3 text-green-600 hover:text-green-800 transition-colors"
                        onClick={() => handleFileClick(attachment)}
                        title="View/Download attachment"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quiet secondary actions at the bottom of the page */}
      <div className="mt-6 mb-4 pt-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3" data-testid="task-details-footer">
        <p className="task-details-noscale text-xs text-slate-500 max-w-md" data-testid="task-details-footer-hint">
          {task.completed ? t('followUpTaskInfo') : t('completionInfo')}
        </p>
        {task.completed ? (
          <button onClick={handleCreateFollowUp} className={FOOTER_BTN} data-testid="create-followup-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
            {t('createFollowUpTask')}
          </button>
        ) : (
          <button onClick={handleCompleteTask} className={FOOTER_BTN_COMPLETE} data-testid="mark-complete-button">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
            {t('markComplete')}
          </button>
        )}
      </div>

      {/* Follow-up Tasks */}
      {followUpTasks.length > 0 && (
        <CollapsibleSection
          label={`Follow-up Tasks (${followUpTasks.length})`}
          open={followUpsOpen}
          onToggle={() => setFollowUpsOpen((v) => !v)}
          testId="followup-tasks-section"
        >
            {followUpTasks.map((followUpTask) => (
              <div key={followUpTask.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <button
                      onClick={() => goToTask(followUpTask.id)}
                      className="text-left hover:text-blue-600 transition-colors"
                    >
                      <h3 className="font-medium text-gray-900 hover:text-blue-600">
                        {followUpTask.title}
                      </h3>
                      {followUpTask.due_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          Due: {new Date(followUpTask.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 ml-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      followUpTask.completed 
                        ? 'bg-green-100 text-green-800' 
                        : isTaskOverdue(followUpTask)
                        ? 'bg-red-100 text-red-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {followUpTask.completed ? 'Completed' : 
                       isTaskOverdue(followUpTask) ? 'Overdue' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </CollapsibleSection>
      )}

      {/* Recurring Tasks */}
      {recurringTasks.length > 0 && (
        <CollapsibleSection
          id="recurring-tasks-section"
          label={`🔄 Recurring Tasks (${recurringTasks.length})`}
          open={recurringOpen}
          onToggle={() => setRecurringOpen((v) => !v)}
          testId="recurring-tasks-section"
        >
            {recurringTasks.map((recurringTask, index) => (
              <div key={recurringTask.id} className="p-3 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <button
                      onClick={() => goToTask(recurringTask.id)}
                      className="text-left hover:text-purple-600 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900 hover:text-purple-600">
                          {recurringTask.title}
                        </h3>
                        {recurringTask.recurring && (
                          <span className="px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs font-medium">
                            Original
                          </span>
                        )}
                      </div>
                      {recurringTask.due_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          Due: {new Date(recurringTask.due_date).toLocaleDateString()}
                        </p>
                      )}
                      {recurringTask.recurring_frequency && (
                        <p className="text-xs text-purple-600 mt-1">
                          Repeats: {recurringTask.recurring_frequency}
                        </p>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 ml-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      recurringTask.completed 
                        ? 'bg-green-100 text-green-800' 
                        : isTaskOverdue(recurringTask)
                        ? 'bg-red-100 text-red-800'
                        : recurringTask.id === task.id
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {recurringTask.completed ? 'Completed' : 
                       isTaskOverdue(recurringTask) ? 'Overdue' :
                       recurringTask.id === task.id ? 'Current' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </CollapsibleSection>
      )}

      </div>{/* end lg:max-w-4xl wrapper */}

      {!embedded && <MobileBottomNav />}

      {/* Task Completion Modal */}
      <TaskCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        task={task}
        onComplete={handleCompletionSuccess}
      />

      {/* Edit Task Modal */}
      {showEditModal && (
        <EditTaskModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          task={task}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Follow-Up Task Modal */}
      {showFollowUpModal && (
        <AddTaskModal
          onClose={() => setShowFollowUpModal(false)}
          onSuccess={handleFollowUpSuccess}
          initialData={{
            title: `Follow-up: ${task.title}`,
            category: task.category,
            contact_id: task.contact_id,
            parent_task_id: task.id,
            priority: 'Medium'
          }}
        />
      )}

      {/* File Viewer Modal */}
      {showFileViewer && currentFile && (
        <FileViewerModal
          isOpen={showFileViewer}
          onClose={() => setShowFileViewer(false)}
          file={currentFile}
        />
      )}

      {/* Follow-Up Tasks Modal */}
      <FollowUpTasksModal
        isOpen={showFollowUpTasksModal}
        onClose={() => setShowFollowUpTasksModal(false)}
        parentTask={task}
      />

      {showMessageContact && contact && (
        <MessageContactModal
          task={task}
          contact={contact}
          onClose={() => setShowMessageContact(false)}
          onSent={() => setMessageHistoryKey((k) => k + 1)}
        />
      )}

    </>,
    headerActions
  );
};

export default TaskDetailsPage;